import React from 'react';
import { useIsSmallView } from '#/hooks/useIsSmallView';
import CloseIcon from '@mui/icons-material/Close';
import { IconButtonProps } from '@mui/material';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

export type StackedDrawerWrapProps = {
    children: React.ReactNode;
    closeButtonProps?: IconButtonProps<React.ElementType>;
    onClose?: () => void;
    priority?: number;
    title?: string;
};

const ELEVATION_SPACING_MULTIPLIER = 40;
const Z_INDEX_OFFSET = 10;
const FALLBACK_APPBAR_HEIGHT = 105;

export const StackedDrawerWrap: React.FC<StackedDrawerWrapProps> = ({ children, closeButtonProps, onClose, priority = 0, title }) => {
    const isSm = useIsSmallView();
    if (!isSm) {
        return children;
    }

    const elevation = priority + 1;

    return (
        <MuiDrawer
            anchor={'bottom'}
            hideBackdrop={true}
            open={true}
            slotProps={{
                paper: {
                    sx: (theme) => {
                        const spacingValue = parseInt(theme.spacing(1)) || 8; // Fallback to 8px if parsing fails
                        const topPosition =
                            elevation >= 1 ? elevation * ELEVATION_SPACING_MULTIPLIER + spacingValue : theme.settings?.appBar.spacer() || 0;

                        return {
                            top: topPosition,
                            bottom: 0,
                            height: 'auto',
                            width: '100%',
                            padding: theme.spacing(2),
                            boxShadow: '0px -6px 6px -3px rgba(0,0,0,0.14)',
                        };
                    },
                },
            }}
            sx={(theme) => ({
                zIndex: elevation > 0 ? elevation + (theme.zIndex.modal - Z_INDEX_OFFSET) : theme.zIndex.appBar + FALLBACK_APPBAR_HEIGHT,
            })}
        >
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 'normal', textTransform: 'none', mx: 'auto', mb: { xs: 1, lg: 2 } }}>
                {title}
                <IconButton
                    sx={(theme) => ({
                        position: 'absolute',
                        top: theme.spacing(1),
                        right: theme.spacing(1),
                    })}
                    aria-label="close"
                    color="inherit"
                    onClick={onClose}
                    {...closeButtonProps}
                >
                    <CloseIcon />
                </IconButton>
            </Typography>
            <Box sx={{ overflowY: 'auto', scrollbarWidth: 'none' }}>{children}</Box>
        </MuiDrawer>
    );
};
